import type { JumpChart } from '@shared/jumpChart';
import type { JumpEngineState } from '@shared/engine/jump/state';
import type {
  JumpDeathShatter,
  JumpFloatingText,
  JumpFlashSpec,
  JumpScorePop,
} from './jumpRenderTypes';
import {
  BIG_BANNER_Y_FRAC,
  CLOSE_CALL_COLOUR,
  DEATH_COLOUR,
  DEATH_SHARD_EXPLODE,
  DEATH_SHARD_GAP,
  DEATH_SHARD_GRID,
  DEATH_SHARD_SPIN,
  VERDICT_COLOURS,
} from './jumpRenderConstants';

/**
 * Paint floating verdict / banner text + score-pop flyers + flashes on top of
 * the playfield, then the countdown / death overlays. Mutates `floatingTexts`,
 * `scorePops`, and `flashes` in place (life counts down by `dt`; expired
 * entries are spliced) so the session never needs a separate update pass.
 */
export function drawJumpOverlays(
  ctx: CanvasRenderingContext2D,
  chart: JumpChart,
  state: JumpEngineState,
  floatingTexts: JumpFloatingText[],
  scorePops: JumpScorePop[],
  flashes: JumpFlashSpec[],
  countdown: number | null,
  canvasWidth: number,
  canvasHeight: number,
  dt: number,
  chromeShadows: boolean,
  deathShatter: JumpDeathShatter | null,
): void {
  drawJumpFlashes(ctx, flashes, canvasWidth, canvasHeight, dt);
  drawFloatingTexts(ctx, floatingTexts, canvasWidth, canvasHeight, dt, chromeShadows);
  drawScorePops(ctx, scorePops, dt, chromeShadows);
  if (countdown !== null) {
    drawCountdown(ctx, countdown, chart, canvasWidth, canvasHeight);
  }
  if (!state.alive) {
    drawDeathOverlay(ctx, canvasWidth, canvasHeight, chromeShadows, deathShatter);
  }
}

function drawJumpFlashes(
  ctx: CanvasRenderingContext2D,
  flashes: JumpFlashSpec[],
  w: number,
  h: number,
  dt: number,
): void {
  for (let i = flashes.length - 1; i >= 0; i -= 1) {
    const f = flashes[i];
    f.t -= dt;
    if (f.t <= 0) {
      flashes.splice(i, 1);
      continue;
    }
    const intensity = f.intensity ?? 1;
    const a = f.t * 1.5 * intensity;
    ctx.save();
    ctx.globalAlpha = Math.min(1, a);
    const colour =
      f.type === 'milestone'
        ? f.colour ?? '#ffd84d'
        : f.type === 'pass'
          ? VERDICT_COLOURS.perfect
          : f.type === 'good'
            ? VERDICT_COLOURS.good
            : f.type === 'close'
              ? CLOSE_CALL_COLOUR
              : f.type === 'jump'
                ? '#16e0ff'
                : DEATH_COLOUR;
    if (f.type === 'death') {
      ctx.fillStyle = colour;
      ctx.fillRect(0, 0, w, h);
    } else if (f.type === 'milestone') {
      ctx.fillStyle = colour;
      const thickness = Math.min(28 * intensity, f.t * 90);
      ctx.fillRect(0, 0, w, thickness);
      ctx.fillRect(0, h - thickness, w, thickness);
    } else {
      const barHeight = Math.min(12 * intensity, f.t * 40);
      ctx.fillStyle = colour;
      ctx.fillRect(0, 0, w, barHeight);
      ctx.fillRect(0, h - barHeight, w, barHeight);
    }
    ctx.restore();
  }
}

function drawFloatingTexts(
  ctx: CanvasRenderingContext2D,
  texts: JumpFloatingText[],
  canvasWidth: number,
  canvasHeight: number,
  dt: number,
  chromeShadows: boolean,
): void {
  ctx.save();
  for (let i = texts.length - 1; i >= 0; i -= 1) {
    const t = texts[i];
    t.life -= dt;
    t.y += t.vy * dt;
    t.vy *= 0.97;
    if (t.life <= 0) {
      texts.splice(i, 1);
      continue;
    }
    const alpha = Math.min(1, t.life / 0.25);
    ctx.globalAlpha = alpha;
    ctx.fillStyle = t.colour;
    if (t.big) {
      const fontPx = 56 * t.scale;
      ctx.font = `800 ${fontPx}px "Space Grotesk", system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      if (chromeShadows) {
        ctx.shadowColor = t.colour;
        ctx.shadowBlur = 18;
      }
      ctx.fillText(t.text, canvasWidth / 2, canvasHeight * BIG_BANNER_Y_FRAC);
      ctx.shadowBlur = 0;
    } else {
      const fontPx = 22 * t.scale;
      ctx.font = `700 ${fontPx}px "Space Grotesk", system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(t.text, t.x, t.y);
    }
  }
  ctx.restore();
}

function drawScorePops(
  ctx: CanvasRenderingContext2D,
  pops: JumpScorePop[],
  dt: number,
  chromeShadows: boolean,
): void {
  if (pops.length === 0) {
    return;
  }
  ctx.save();
  for (let i = pops.length - 1; i >= 0; i -= 1) {
    const p = pops[i];
    p.life -= dt;
    if (p.life <= 0) {
      pops.splice(i, 1);
      continue;
    }
    const progress = 1 - p.life / p.maxLife;
    const x = p.startX + (p.targetX - p.startX) * progress;
    const y = p.startY + (p.targetY - p.startY) * progress;
    const alpha = Math.min(1, p.life / 0.2);
    ctx.globalAlpha = alpha;
    ctx.fillStyle = p.colour;
    if (chromeShadows) {
      ctx.shadowColor = p.colour;
      ctx.shadowBlur = 8;
    }
    ctx.font = p.big
      ? '800 24px "Space Grotesk", system-ui, sans-serif'
      : '700 18px "Space Grotesk", system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(p.text, x, y);
    ctx.shadowBlur = 0;
  }
  ctx.restore();
}

function drawCountdown(
  ctx: CanvasRenderingContext2D,
  countdownBeats: number,
  chart: JumpChart,
  canvasWidth: number,
  canvasHeight: number,
): void {
  ctx.save();
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = chart.palette.ui;
  ctx.font = '700 84px "Space Grotesk", system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const remaining = Math.ceil(countdownBeats);
  ctx.fillText(remaining.toString(), canvasWidth / 2, canvasHeight * 0.5);
  ctx.font = '500 18px "Space Grotesk", system-ui, sans-serif';
  ctx.globalAlpha = 0.6;
  ctx.fillText('Get ready', canvasWidth / 2, canvasHeight * 0.5 + 70);
  ctx.restore();
}

function drawDeathOverlay(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  chromeShadows: boolean,
  deathShatter: JumpDeathShatter | null,
): void {
  ctx.save();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);
  // The cube's shards sit on the darkened backdrop (painted here, after the
  // death flash) so the shatter reads instead of being washed out by the flash.
  if (deathShatter) {
    drawDeathShatter(ctx, deathShatter, chromeShadows);
  }
  ctx.fillStyle = DEATH_COLOUR;
  ctx.font = '800 72px "Space Grotesk", system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  if (chromeShadows) {
    ctx.shadowColor = DEATH_COLOUR;
    ctx.shadowBlur = 24;
  }
  ctx.fillText('GAME OVER', canvasWidth / 2, canvasHeight * 0.5);
  ctx.restore();
}

/**
 * The cube's frozen "exploded" pose: a grid of shards flung radially outward
 * from the cube centre, each tumbled a deterministic amount. A single static
 * snapshot (no animation) — the death freeze-frame renders exactly once, so the
 * shatter has to land in one frame.
 */
function drawDeathShatter(
  ctx: CanvasRenderingContext2D,
  shatter: JumpDeathShatter,
  chromeShadows: boolean,
): void {
  const half = shatter.size / 2;
  const cell = shatter.size / DEATH_SHARD_GRID;
  const shardSize = cell * (1 - DEATH_SHARD_GAP * 2);
  const explode = half * DEATH_SHARD_EXPLODE;
  ctx.save();
  ctx.translate(shatter.x, shatter.y);
  ctx.rotate(shatter.rotation);
  ctx.fillStyle = shatter.colour;
  if (chromeShadows) {
    ctx.shadowColor = shatter.colour;
    ctx.shadowBlur = 16;
  }
  for (let gx = 0; gx < DEATH_SHARD_GRID; gx += 1) {
    for (let gy = 0; gy < DEATH_SHARD_GRID; gy += 1) {
      const localX = -half + cell * (gx + 0.5);
      const localY = -half + cell * (gy + 0.5);
      const dist = Math.hypot(localX, localY) || 1;
      const offsetX = localX + (localX / dist) * explode;
      const offsetY = localY + (localY / dist) * explode;
      const spin = ((((gx * 3 + gy * 7) % 5) / 5) * 2 - 1) * DEATH_SHARD_SPIN;
      ctx.save();
      ctx.translate(offsetX, offsetY);
      ctx.rotate(spin);
      ctx.fillRect(-shardSize / 2, -shardSize / 2, shardSize, shardSize);
      ctx.restore();
    }
  }
  ctx.restore();
}
